import {
  COLLECT_SAMPLE_PATH,
  QUESTIONNAIRES_PATH,
} from '@/constants/paths';
import { EventTracker } from '@/utils';
import { cn } from '@/utils/styles';
import { Button } from '@design-system/Button';
import { Card, CardContent } from '@design-system/Card';
import { Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { KEYS, LocalStorage } from '@utils/LocalStorage';
import { useQuestionnaireProcessed } from '@hooks/useQuestionnaireProcessed';

interface HomeJourneyCardProps {
  label: string;
  description: string;
  completed: boolean;
  showProgress?: boolean;
  progressValue?: number;
  showContinue?: boolean;
  className?: string;
  isRetest?: boolean;
  stepId: string;
  'data-testid'?: string;
  isQuestionnaireProcessed: boolean;
}

export const HomeJourneyCard = ({
  label,
  description,
  completed,
  showProgress,
  progressValue,
  showContinue,
  className,
  isRetest,
  stepId,
  isQuestionnaireProcessed,
  'data-testid': testId,
}: HomeJourneyCardProps) => {
  const navigate = useNavigate();
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { enableNewQuestionnaire } =
    localStorageUser && JSON.parse(localStorageUser);
  const { setFromCollectSample, setIsQuestionnaireProcessed } =
    useQuestionnaireProcessed();

  const handleRedirection = () => {
    if (stepId === '3') {
      EventTracker.trackEvent('home', 'continue_questionnaire');
      setFromCollectSample(false);
      return navigate(QUESTIONNAIRES_PATH);
    } else if (stepId === '2' && !enableNewQuestionnaire) {
      EventTracker.trackEvent('home', 'contiune_collecttion');
      setFromCollectSample(false);
      return navigate(COLLECT_SAMPLE_PATH);
    } else if (stepId === '2' && enableNewQuestionnaire) {
      EventTracker.trackEvent('home', 'collect-sample-new-questionnaire');
      setFromCollectSample(true);
      setIsQuestionnaireProcessed(isQuestionnaireProcessed);
      return navigate(`/questionnaires/1/questions`);
    }
  };

  return (
    <Card
      data-testid={testId}
      className={cn(
        'relative',
        !isRetest &&
          'bg-background-blur-medium before:absolute before:shadow-glass-inner',
        isRetest && [
          'bg-[#5B5C644D]',
          'backdrop-blur-[50px]',
          'shadow-[0px_1px_0px_0px_#FFFFFF0A_inset,0px_1px_3px_0px_#0000001A]',
          'border-0',
        ],
        (stepId === '2' || stepId === '3') && 'cursor-pointer',
        className,
      )}
      onClick={() => handleRedirection()}
    >
      <CardContent className="flex flex-col gap-4 p-6">
        <div className="flex items-start gap-3">
          {completed ? (
            <div className="rounded-full p-1 bg-background-success text-white">
              <Check className="h-4 w-4" />
            </div>
          ) : (
            <div
              className={cn(
                'w-5 h-5 rounded-full flex items-center justify-center',
                !isRetest && 'border',
                isRetest && 'border border-[#C8CFDB80]',
              )}
            />
          )}

          <div className="flex flex-col gap-1 flex-1">
            <span
              data-testid="journeyCard-label"
              className={cn(
                'typography-h3',
                !isRetest && 'text-primary',
                isRetest && 'text-[#C8CFDBCC]',
              )}
            >
              {label}
            </span>
            <span
              data-testid="journeyCard-description"
              className={cn(
                'typography-body',
                !isRetest && 'text-tertiary',
                isRetest && 'text-[#C8CFDB80]',
              )}
            >
              {description}
            </span>
          </div>
        </div>

        {showProgress && !completed && (
          <div className="w-full h-1 bg-background-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-background-success transition-all duration-300 ease-in-out"
              style={{ width: `${progressValue}%` }}
            />
          </div>
        )}

        {showContinue && (
          <div className="flex justify-start">
            <Button
              variant="ghost"
              data-testid="journeyCard-continueButton"
              className={cn(
                'bg-transparent text-success hover:text-success/90 px-4',
                '[&>svg]:ml-2',
              )}
            >
              Continue
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
